import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { fetchMaterialRawText, RawTextPage } from '../../services/knowledgeApi';

const PAGE_SIZE = 20;

/** The text read from a file, page by page, before splitting or AI organizing. */
export const RawTextDialog: React.FC<{ materialId: string; title: string; onClose: () => void }> = ({ materialId, title, onClose }) => {
  const { getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [page, setPage] = useState(1);
  const [data, setData] = useState<RawTextPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMaterialRawText(() => tokenRef.current(), materialId, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [materialId, page]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const unit = data?.kind === 'rows' ? 'row' : data?.kind === 'slides' ? 'slide' : data?.kind === 'pages' ? 'page' : 'section';

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Original text: {title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The text read from the file, before it was split or organized. Searches use the snippets, not this.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {loading && !data && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Reading the file. Scanned PDFs can take a minute the first time.
            </Typography>
          </Stack>
        )}
        {data && (
          <>
            <Typography variant="caption" color="text.secondary">
              {data.total.toLocaleString()} {unit}
              {data.total === 1 ? '' : 's'} · {data.chars.toLocaleString()} characters
              {data.ocr_pages ? ` · ${data.ocr_pages} scanned page${data.ocr_pages === 1 ? '' : 's'} read with OCR` : ''}
            </Typography>
            {data.header && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                Columns: {data.header}
              </Alert>
            )}
            <Stack spacing={1} sx={{ mt: 1, opacity: loading ? 0.5 : 1 }}>
              {data.items.map((s, i) => (
                <Paper key={`${page}-${i}`} variant="outlined" sx={{ p: 1.25 }}>
                  {(s.label || s.page) && (
                    <Chip size="small" label={(s.label || `Page ${s.page}`).split('\n')[0]} sx={{ mb: 0.5 }} />
                  )}
                  <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {s.text}
                  </Typography>
                </Paper>
              ))}
            </Stack>
            {pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} size="small" />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
